import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Mail, KeyRound } from "lucide-react";

const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors placeholder:text-[#555]";

export const AccountSettings = ({ onClose }) => {
  const { user, changePassword, changeEmail } = useAuth();
  const [tab, setTab] = useState("password");

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [em, setEm] = useState({ current_password: "", new_email: "" });
  const [busy, setBusy] = useState(false);

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await changePassword(pw.current_password, pw.new_password);
      toast.success("Password updated");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await changeEmail(em.current_password, em.new_email);
      toast.success("Email updated");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="account-settings-modal" onClick={onClose}>
      <div className="w-full max-w-md border border-[#2A2A2A] bg-[#111111]" onClick={(e) => e.stopPropagation()}>
        <div className="h-1 bg-[#FF4D00]" />
        <div className="p-7">
          <div className="flex items-start justify-between mb-1">
            <h3 className="font-bebas text-3xl tracking-wide">ACCOUNT SETTINGS</h3>
            <button data-testid="account-close-btn" onClick={onClose} className="text-[#A1A1AA] hover:text-white transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-xs text-[#A1A1AA] mb-6">Signed in as <span className="text-white">{user?.email}</span></p>

          <div className="flex border-b border-[#2A2A2A] mb-6">
            <button data-testid="tab-change-password" onClick={() => setTab("password")}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.1em] border-b-2 -mb-px transition-colors ${tab === "password" ? "border-[#FF4D00] text-white" : "border-transparent text-[#A1A1AA] hover:text-white"}`}>
              <KeyRound className="w-3.5 h-3.5" /> Password
            </button>
            <button data-testid="tab-change-email" onClick={() => setTab("email")}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.1em] border-b-2 -mb-px transition-colors ${tab === "email" ? "border-[#FF4D00] text-white" : "border-transparent text-[#A1A1AA] hover:text-white"}`}>
              <Mail className="w-3.5 h-3.5" /> Email
            </button>
          </div>

          {tab === "password" ? (
            <form onSubmit={savePassword} className="space-y-4" data-testid="change-password-form">
              <input data-testid="current-password-input" type="password" className={inputCls} placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required />
              <input data-testid="new-password-input" type="password" className={inputCls} placeholder="New password (min 8 chars)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required />
              <input data-testid="confirm-password-input" type="password" className={inputCls} placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required />
              <button data-testid="save-password-btn" disabled={busy} className="w-full bg-[#FF4D00] text-white py-3 hover:bg-[#E64500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
              </button>
            </form>
          ) : (
            <form onSubmit={saveEmail} className="space-y-4" data-testid="change-email-form">
              <input data-testid="new-email-input" type="email" className={inputCls} placeholder="New email address" value={em.new_email} onChange={(e) => setEm({ ...em, new_email: e.target.value })} required />
              <input data-testid="email-current-password-input" type="password" className={inputCls} placeholder="Current password" value={em.current_password} onChange={(e) => setEm({ ...em, current_password: e.target.value })} required />
              <button data-testid="save-email-btn" disabled={busy} className="w-full bg-[#FF4D00] text-white py-3 hover:bg-[#E64500] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
